import React from "react";
import { Typography, Box } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from "recharts";

// Color palette for different products and shocks
const PRODUCT_COLORS = {
  'Product1': '#8884d8',
  'Product2': '#ff7300',
  'Product3': '#00C49F',
  'Product4': '#ffc658',
  'Product5': '#82ca9d'
};

const SHOCK_VARIANTS = {
  'No Shock': 1.0,
  'Up 100 shock': 0.8,
  'Down 100 shock': 0.6
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hexColor, factor) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Apply brightness factor
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

export default function AssetsBvChart({ datasets = [], shockSelections = ['No Shock'], scaleEnabled = false }) {
  console.log('AssetsBvChart props:', { datasets, shockSelections });
  
  // Process each dataset for each selected shock
  const processedDatasets = datasets.map((dataset, datasetIndex) => {
    return shockSelections.map(shock => {
      // Get the appropriate data field based on shock selection
      const dataField = shock === 'Up 100 shock' ? 'data_up' : 
                       shock === 'Down 100 shock' ? 'data_down' : 
                       'data';
      
      const chartData = dataset.data?.[dataField] || [];
      
      console.log(`Dataset ${dataset.name}, Shock ${shock}, DataField ${dataField}:`, chartData);
      
      // Apply scaling if enabled
      const processedChartData = scaleEnabled && Array.isArray(chartData) 
        ? chartData.map(point => {
            const scalar = point.scalar || 1;
            return {
              ...point,
              TargetAssets: (point.TargetAssets || 0) * scalar,
              RUNOFF_BV: (point.RUNOFF_BV || 0) * scalar
            };
          })
        : chartData;
      
      // Get base color for the product
      const baseColor = PRODUCT_COLORS[dataset.name] || PRODUCT_COLORS[`Product${(datasetIndex % 5) + 1}`];
      
      // Apply shock variant to create different shades
      const shockVariant = SHOCK_VARIANTS[shock] || 1.0;
      const color = adjustColorBrightness(baseColor, shockVariant);
      
      return {
        ...dataset,
        shock,
        chartData: processedChartData,
        color
      };
    });
  }).flat();

  console.log('Processed datasets:', processedDatasets);

  // Filter valid datasets
  const validDatasets = processedDatasets.filter(d => 
    Array.isArray(d.chartData) && d.chartData.length > 0
  );

  console.log('Valid datasets:', validDatasets);

  // Check for errors
  const hasErrors = processedDatasets.some(d => 
    d.chartData && !Array.isArray(d.chartData) && d.chartData.error
  );

  if (hasErrors) {
    return (
      <Box sx={{ width: '100%', height: 400, overflow: 'visible' }}>
        <Typography variant="body1" color="error">
          Error loading data for some shock scenarios.
        </Typography>
      </Box>
    );
  }

  if (validDatasets.length === 0) {
    return (
      <Box sx={{ width: '100%', height: 400, overflow: 'visible' }}>
        <Typography variant="body1" color="text.secondary">
          No data available for the selected shock scenarios.
        </Typography>
      </Box>
    );
  }

  // Collect all unique dates
  const allDates = new Set();
  validDatasets.forEach(d => 
    d.chartData.forEach(item => item.Date && allDates.add(item.Date))
  );

  // Build combined chart data
  const combinedChartData = Array.from(allDates)
    .sort()
    .map(date => {
      const point = { Date: date };
      validDatasets.forEach(d => {
        const found = d.chartData.find(item => item.Date === date);
        if (found) {
          const prefix = `${d.name}_${d.shock}`.replace(/\s/g, '_');
          point[`${prefix}_TargetAssets`] = found.TargetAssets;
          point[`${prefix}_RUNOFF_BV`] = found.RUNOFF_BV;
        }
      });
      return point;
    });

  const isValidData = Array.isArray(combinedChartData) && combinedChartData.length > 0;
  const defaultStartIndex = 0;
  const defaultEndIndex = isValidData ? Math.min(10, combinedChartData.length - 1) : 0;

  return (
    <Box sx={{ width: '100%', height: 400, overflow: 'visible' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={combinedChartData}
          margin={{ left: 15 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="Date" tick={{ fontSize: 12 }} />
          <YAxis
            axisLine={{ stroke: '#333' }}
            tickLine={{ stroke: '#333' }}
            tick={{ fill: '#333', fontSize: 12 }}
            tickFormatter={val => `$${val.toLocaleString()}`}
          />
          <Tooltip 
            formatter={(value, name) => [
              typeof value === 'number' ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value,
              name
            ]}
          />
          <Legend 
            verticalAlign="bottom" 
            align="center" 
            height={36}
            payload={validDatasets.flatMap(d => {
              const prefix = `${d.name}_${d.shock}`.replace(/\s/g, '_');
              return [
                {
                  value: `${d.name} – Liabilities BV (${d.shock})`,
                  color: d.color,
                  id: `${prefix}-assets`,
                  type: 'plainline',
                  payload: { strokeDasharray: '0' }
                },
                {
                  value: `${d.name} – Assets BV (${d.shock})`,
                  color: d.color,
                  id: `${prefix}-liabilities`,
                  type: 'line'
                }
              ];
            })}
          />
          {validDatasets.flatMap(d => {
            const prefix = `${d.name}_${d.shock}`.replace(/\s/g, '_');
            return [
              <Line
                key={`${prefix}-assets`}
                type="monotone"
                dataKey={`${prefix}_TargetAssets`}
                name={`${d.name} – Liabilities BV (${d.shock})`}
                stroke={d.color}
                strokeWidth={2}
                dot={false}
              />,              
              <Line
                key={`${prefix}-liabilities`}
                type="monotone"
                dataKey={`${prefix}_RUNOFF_BV`}
                name={`${d.name} – Assets BV (${d.shock})`}
                stroke={d.color}
                strokeWidth={2}
                dot={true}
              />,
              <Brush
                dataKey="Date"
                startIndex={defaultStartIndex}
                endIndex={defaultEndIndex}
                height={30}
                stroke="#8884d8"
              />
            ];
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
